import deepEqual from 'fast-deep-equal'

import { doAppGraphqlRequest } from 'ducks/requests'
import { doUpdateCustomFieldFileValues } from 'ducks/crm/customFields/operations/files'
import {
  selectCustomFieldForKey,
  selectCustomFieldWithHandleTypes,
} from 'ducks/crm/customFields/selectors/base'
import { all } from 'util/arrays'
import { readAsDataURL } from 'util/file'
import { selectIsInChat } from 'selectors/location'
import { selectCompanytById } from 'ducks/crm/companies/selectors/base'
import { isCompanyId } from 'ducks/crm/companies/utils'
import { sleep } from 'util/functions'

import {
  changeContactMutationSchema,
  createContactMutationSchema,
  customFieldValueCompanyNormalizationScheme,
  customFieldValueContactNormalizationScheme,
} from '../schema'
import {
  selectContactById,
  selectCustomFieldValueForCustomFieldKeyAndSubjectId,
} from '../selectors'
import {
  CHANGE_CONVERSATION_CONTACT,
  CREATE_CONTACT,
  UPDATE_CUSTOM_FIELD_VALUE,
  BULK_UPDATE_CUSTOM_FIELD_VALUE,
  RESYNC_CONTACT,
  MERGE_CONTACT,
} from '../types'
import { customFieldValueNodeQuery } from '../../customFields/operations/fetching'
import {
  doFetchCurrentContact,
  doFetchContactForPrimaryEmail,
} from './fetching'

const mergeContactQuery = `
mutation ContactMerge(
  $sourceId: ID!,
  $targetId: ID!,
  $keepFieldIds: [ID!]
) {
  contactMerge(input: {
    sourceId: $sourceId,
    targetId: $targetId,
    keepFieldIds: $keepFieldIds
  }) {
    target {
      id
    }
    errors {
      message
      path
    }
  }
}
`

const bulkUpdateCustomFieldValueQuery = `
mutation CustomFieldValuesBulkUpdate(
  $items: [SubjectWithCustomFieldsInput!]!,
  $sync: Boolean!
) {
  customFieldValuesBulkUpdate(input: { items: $items, sync: $sync }) {
    jid
  }
}
`
export const doMergeContact = (
  sourceContactId,
  targetContactId,
  keepFieldIds,
  options = {}
) => async dispatch => {
  const variables = {
    sourceId: sourceContactId,
    targetId: targetContactId,
    keepFieldIds,
  }

  return dispatch(
    doAppGraphqlRequest(MERGE_CONTACT, mergeContactQuery, variables, options)
  )
}

export const doResolveEmailConflict = (
  sourceContact,
  targetContactEmail
) => async (dispatch, getState) => {
  const targetContact = await dispatch(
    doFetchContactForPrimaryEmail(targetContactEmail)
  )

  if (targetContact) {
    // When the existing contact already has an email address it means that we're
    // essentially attemping to change the contact. The way to resolve this is to
    // remove the matrix chat id from the current contact and add it to the target
    // contact
    if (sourceContact.email) {
      await dispatch(
        doAppGraphqlRequest(
          BULK_UPDATE_CUSTOM_FIELD_VALUE,
          bulkUpdateCustomFieldValueQuery,
          {
            sync: true,
            items: [
              // Remove contact_matrix_id from the source
              {
                subjectId: sourceContact.id,
                customFieldList: [
                  {
                    customFieldIdOrKey: 'contact_matrix_id',
                    remove: true,
                  },
                ],
              },
              // Add contact_matrix_id from the target
              {
                subjectId: targetContact.id,
                customFieldList: sourceContact.customFieldValues.contact_matrix_id.value.map(
                  ({ content: matrixEnduserId }) => ({
                    customFieldIdOrKey: 'contact_matrix_id',
                    value: matrixEnduserId,
                    mode: 'APPEND',
                    remove: false,
                  })
                ),
              },
            ],
          },
          {}
        )
      )
      // If the current contact doesnt have an email address and that email address
      // already exists in the system, then it simply means we want to get rid of the
      // current contact and merge any identifyable information into the target contact
    } else {
      const state = getState()
      const handleCustomFields = selectCustomFieldWithHandleTypes(state)
      const handleCustomFieldsIds = handleCustomFields.map(cf => cf.id)

      await dispatch(
        doMergeContact(
          sourceContact.id,
          targetContact.id,
          handleCustomFieldsIds
        )
      )
    }

    // After we've changed the contact or merged the contact we need to
    // refetch the current pages contact to make sure we get the updated
    // version
    let attempt = 0
    while (attempt < 30) {
      // eslint-disable-next-line no-await-in-loop
      const fetchedContactId = await dispatch(
        doFetchCurrentContact({
          allowCreateContact: false,
          disableSuccessActions: true,
        })
      )
      if (fetchedContactId === targetContact.id) break

      // eslint-disable-next-line no-await-in-loop
      await sleep(5000)
      attempt += 1
    }

    // Pull the updated contact from state and return it
    return selectContactById(getState(), targetContact.id)
  }

  // If we cant resolve the conflict, return null to signal to the upstream method that it couldnt be fixed
  return null
}

const doHandleCustomFieldValuesUpdateErrors = (
  subject,
  fields,
  response
) => async dispatch => {
  const isSubjectCompany = isCompanyId(subject.id)
  if (
    !isSubjectCompany &&
    response.customFieldValuesUpdate.errors &&
    response.customFieldValuesUpdate.errors.length > 0
  ) {
    const errors = response.customFieldValuesUpdate.errors
    const contactEmailFieldError = errors.find(error => {
      const fieldIndex = error.path[2]
      const field = fields[fieldIndex]
      return (
        field.key === 'contact_email' &&
        error.message.endsWith(': has already been taken')
      )
    })
    if (contactEmailFieldError) {
      const fieldIndex = contactEmailFieldError.path[2]
      const {
        value: { content: email },
      } = fields[fieldIndex]

      await dispatch(doResolveEmailConflict(subject, email))
    }
  }
}

const updateCustomFieldValueQuery = `
mutation UpdateCustomFieldValue(
  $input: CustomFieldValuesUpdateInput!,
  $customFieldIds: [ID!]
) {
  customFieldValuesUpdate(input: $input) {
    subject {
      ... on Node {
        id
      }
      ... on CustomFieldValuesField {
        customFieldValues(filter: { customFieldIds: $customFieldIds }) {
          ${customFieldValueNodeQuery}
        }
      }
    }

    errors {
      message
      path
      type
    }
  }
}
`

export function doUpdateCustomFieldValues(subjectId, fields) {
  return async (dispatch, getState) => {
    const state = getState()
    const fileFields = fields.filter(
      ({ isFileField, remove }) => isFileField && !remove
    )
    const nonFileFields = fields.filter(
      ({ isFileField, remove }) => !isFileField || remove
    )
    const isSubjectCompany = isCompanyId(subjectId)

    const subject = isSubjectCompany
      ? selectCompanytById(state, subjectId)
      : selectContactById(state, subjectId)

    const subjectKey = isSubjectCompany ? 'companies' : 'contacts'
    const fileFieldsWithDataUrls = await Promise.all(
      fileFields.map(field => {
        return readAsDataURL(field.value).then(dataUrl => ({
          ...field,
          dataUrl,
        }))
      })
    )
    const optimist = {
      entities: {
        [subjectKey]: {
          [subjectId]: {
            id: subjectId,
            customFieldValues: {
              nodes: [
                ...nonFileFields.map(
                  ({ key, isArray, mode, value, valueId }) => ({
                    id: valueId,
                    customField: { isArray },
                    key,
                    value,
                    mode,
                  })
                ),
                ...fileFieldsWithDataUrls.map(({ dataUrl, key, valueId }) => ({
                  id: valueId,
                  key,
                  value: { dataUrl },
                })),
              ],
            },
          },
        },
      },
    }
    const attachments = await (fileFields.length > 0
      ? dispatch(
          doUpdateCustomFieldFileValues(fileFields, {
            optimist,
            meta: {
              mergeEntities: true,
            },
          })
        )
      : Promise.resolve([]))
    const customFieldList = [
      ...nonFileFields.map(({ id, mode, value }) => {
        const newValue =
          value && deepEqual(Object.keys(value), ['content'])
            ? value.content
            : value
        return {
          customFieldIdOrKey: id,
          ...(['', undefined, null].includes(newValue)
            ? { remove: true }
            : { value: newValue, mode }),
        }
      }),
      ...attachments.map(({ id, value }) => {
        return {
          customFieldIdOrKey: id,
          value: {
            content_type: value.attachment_content_type,
            file_name: value.attachment_file_name,
            file_size: value.attachment_file_size,
            attachment_id: value.id,
          },
        }
      }),
    ]
    if (customFieldList.length === 0) return null
    const variables = {
      input: {
        subjectId,
        customFieldList,
      },
      customFieldIds: fields.map(({ id }) => id),
    }
    const response = await dispatch(
      doAppGraphqlRequest(
        UPDATE_CUSTOM_FIELD_VALUE,
        updateCustomFieldValueQuery,
        variables,
        {
          optimist,
          meta: {
            mergeEntities: true,
            attachments,
          },
          normalizationSchema: isSubjectCompany
            ? customFieldValueCompanyNormalizationScheme
            : customFieldValueContactNormalizationScheme,
          transformEntities: entitities => {
            const subjects = entitities[subjectKey] || {}
            Object.keys(subjects).forEach(id => {
              // eslint-disable-next-line no-param-reassign
              subjects[id].customFieldValues =
                subjects[id].customFieldValues || {}
            })
            return entitities
          },
        }
      )
    )

    await dispatch(
      doHandleCustomFieldValuesUpdateErrors(subject, fields, response)
    )

    return response
  }
}

function valueIsEmpty(value) {
  return (
    !value || all(x => ['', undefined, null].includes(x), Object.values(value))
  )
}

export function doUpdateCustomFieldValuesFromForm(subjectId, formData) {
  return (dispatch, getState) => {
    const state = getState()
    const doneKeys = []

    const newValues = Object.keys(formData)
      .reduce((result, key) => {
        const { id: valueId, value: existingValue } =
          selectCustomFieldValueForCustomFieldKeyAndSubjectId(state, {
            customFieldKey: key,
            subjectId,
          }) || {}
        const { id, isArray, type } =
          selectCustomFieldForKey(state, { key }) || {}

        const newValue = formData[key]
        if (isArray) {
          const filteredNewValue = newValue.filter(
            ({ content } = {}) => !!content
          )
          return result.concat(
            filteredNewValue.length > 0
              ? filteredNewValue.map((individualValue, index) => {
                  return {
                    key,
                    id,
                    isArray: true,
                    isFileField: false,
                    mode: index === 0 ? 'REPLACE' : 'APPEND',
                    valueId,
                    value: individualValue,
                  }
                })
              : [
                  {
                    key,
                    id,
                    isArray: true,
                    isFileField: false,
                    mode: 'REPLACE',
                    valueId,
                    remove: true,
                  },
                ]
          )
        }
        const isFileField = type === 'FILE' && newValue instanceof File
        const bothEmpty = valueIsEmpty(newValue) && valueIsEmpty(existingValue)
        // If new and existing values are equivalent return early
        const equalValues = deepEqual(newValue, existingValue)
        const unchangedFile =
          isFileField && newValue.remove === 'false' && newValue.size === 0
        const isFileRemoved = newValue.remove === 'true'
        delete newValue.remove
        if ((equalValues || bothEmpty) && !isFileField) return result
        if (unchangedFile) return result
        const firstForKey = !doneKeys.includes(key)
        doneKeys.push(key)
        result.push({
          key,
          id,
          isFileField,
          firstForKey,
          valueId,
          value: isFileRemoved ? null : newValue,
          remove: isFileRemoved,
        })
        return result
      }, [])
      .filter(x => x !== null)
    if (newValues.length > 0) {
      dispatch(doUpdateCustomFieldValues(subjectId, newValues))
    }
  }
}

const changeContactMutation = `
mutation changeContactMutation($conversationId: ID!, $contactId: ID!) {
  conversationChangeContact(input: { conversationId: $conversationId, contactId: $contactId }) {
    conversation {
      id
      number
      contact {
        id
      }
    }
  }
}
`
export function doChangeContactForConversation(
  conversationId,
  conversationNumber,
  contactId
) {
  return dispatch => {
    const variables = {
      conversationId,
      contactId,
    }
    return dispatch(
      doAppGraphqlRequest(
        CHANGE_CONVERSATION_CONTACT,
        changeContactMutation,
        variables,
        {
          optimist: {
            conversationNumber,
            contactId,
          },
          meta: {
            mergeEntities: true,
          },
          normalizationSchema: changeContactMutationSchema,
        }
      )
    )
  }
}

const createContactMutation = `
mutation createContactMutation($customFields: [CustomFieldInput!]) {
  contactCreate(input: { contactType: contact, customFields: $customFields }) {
    contact {
      id
    }
  }
}

`
export function doCreateAndChangeContactForConversation(
  contactIdOrTemp, // could be a temp conv id, needed to mark isCreating flag
  conversationId,
  conversationNumber,
  name,
  email,
  contactMatrixId
) {
  return (dispatch, getState) => {
    const state = getState()
    const isInChat = selectIsInChat(state)
    const { id: emailId } = selectCustomFieldForKey(state, {
      key: 'contact_email',
    })
    const { id: firstNameId } = selectCustomFieldForKey(state, {
      key: 'contact_first_name',
    })
    const { id: lastNameId } = selectCustomFieldForKey(state, {
      key: 'contact_last_name',
    })
    const { id: contactMatrixFieldId } = selectCustomFieldForKey(state, {
      key: 'contact_matrix_id',
    })
    const [, firstName, lastName] = name.match(/^(.+) ([^ ]+)$/) || [null, name]
    return dispatch(
      doAppGraphqlRequest(
        CREATE_CONTACT,
        createContactMutation,
        {
          customFields: [
            email && { customFieldIdOrKey: emailId, value: email },
            firstName && { customFieldIdOrKey: firstNameId, value: firstName },
            lastName && { customFieldIdOrKey: lastNameId, value: lastName },
            contactMatrixId && {
              customFieldIdOrKey: contactMatrixFieldId,
              value: contactMatrixId,
            },
          ].filter(x => !!x),
        },
        {
          normalizationSchema: createContactMutationSchema,
          meta: {
            contactIdOrTemp,
          },
        }
      )
    )
      .then(result => {
        const { contactCreate: { contact } = {} } = result || {}
        const { id } = contact || {}
        // in chat we fetch conversations by matrix id custom field, not by conversation id
        // so no need to link. API throws error otherwise
        if (!isInChat && id) {
          return dispatch(
            doChangeContactForConversation(
              conversationId,
              conversationNumber,
              id
            )
          )
        }
        return result
      })
      .then(result => {
        const changeContactId =
          result?.conversationChangeContact?.conversation?.contact?.id
        const createContactId = result?.contactCreate?.contact?.id

        const contactId = isInChat ? createContactId : changeContactId
        const hasCreatedContactId = !!contactId
        // reload contact page, only if the change and/or create contact executes successfully
        if (hasCreatedContactId) {
          return dispatch(doFetchCurrentContact())
        }

        return contactId
      })
  }
}

const resyncContactMutation = `
mutation resyncContactMutation($contactId: ID!, $integrationId: ID!) {
  contactResync(input: { contactId: $contactId, integrationId: $integrationId,  }) {
    contact {
      id
    }
    errors {
      message
      path
    }
  }
}
`

export const doContactResyncIntegrationData = (
  contactId,
  integrationId,
  // eslint-disable-next-line no-unused-vars
  options = {}
) => async dispatch => {
  await dispatch(
    doAppGraphqlRequest(
      RESYNC_CONTACT,
      resyncContactMutation,
      {
        contactId,
        integrationId,
      },
      {}
    )
  )
  return dispatch(doFetchCurrentContact())
}
